export const APPLICATION_COMPOSITION_METHODOLOGY_SCHEMA_VERSION =
  "cogo.application-composition-methodology.v1";

export const FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES = [
  "container",
  "text",
  "image",
  "button",
  "input",
  "select",
  "checkbox",
  "switch",
  "tabs",
  "list",
  "card",
  "chart",
  "data_table",
  "form",
  "nav",
  "dialog",
  "table",
] as const;

export const FIXED_DESIGN_SYSTEM_PARTS = [
  "color",
  "typography",
  "spacing",
  "radius",
  "shadow",
  "density",
  "surface",
  "componentStyleRule",
  "stateStyleRule",
  "responsiveStyleRule",
] as const;

export type LayoutPatternProfile = {
  readonly patternId: string;
  readonly layoutKind: string;
  readonly slots: readonly string[];
  readonly constraints: readonly string[];
  readonly responsiveBehavior: string;
  readonly scrollModel: string;
  readonly densityProfile: string;
  readonly applicableContentModes: readonly string[];
};

export type BlockPatternProfile = {
  readonly patternId: string;
  readonly purpose: string;
  readonly requiredComponents: readonly string[];
  readonly optionalComponents: readonly string[];
  readonly layoutPatternRefs: readonly string[];
  readonly dataInputs: readonly string[];
  readonly actionOutputs: readonly string[];
  readonly designTokenRequirements: readonly string[];
};

export type PagePatternProfile = {
  readonly patternId: string;
  readonly routeScope: string;
  readonly layoutPatternRefs: readonly string[];
  readonly requiredBlockPatternRefs: readonly string[];
  readonly optionalBlockPatternRefs: readonly string[];
  readonly stateScope: string;
  readonly actionScope: string;
};

export type ApplicationInstanceFixture = {
  readonly fixtureId: string;
  readonly domainLabel: string;
  readonly pagePatternRefs: readonly string[];
  readonly blockPatternRefs: readonly string[];
  readonly layoutPatternRefs: readonly string[];
  readonly componentVocabularyExtended: boolean;
  readonly designSystemStructureExtended: boolean;
  readonly domainSpecificUiOntologyPromoted: boolean;
  readonly designSystemValueSet: string;
  readonly dataModelLabel: string;
};

export const APPLICATION_LAYOUT_PATTERN_PROFILES: readonly LayoutPatternProfile[] = [
  {
    patternId: "stack",
    layoutKind: "stack",
    slots: ["items"],
    constraints: ["single-axis-order", "tokenized-gap"],
    responsiveBehavior: "axis and gap may change by breakpoint; component vocabulary does not change",
    scrollModel: "inherits-parent-scroll",
    densityProfile: "regular-or-dense",
    applicableContentModes: ["form", "detail", "article-body", "settings"],
  },
  {
    patternId: "grid",
    layoutKind: "grid",
    slots: ["cells"],
    constraints: ["declared-template", "stable-gutter", "responsive-track-count"],
    responsiveBehavior: "track count and minmax constraints adapt by breakpoint",
    scrollModel: "page-or-container-scroll",
    densityProfile: "regular-or-dense",
    applicableContentModes: ["catalog", "dashboard", "media-index", "metric-summary"],
  },
  {
    patternId: "split",
    layoutKind: "split",
    slots: ["primary", "secondary"],
    constraints: ["explicit-ratio-or-minmax", "collapse-order"],
    responsiveBehavior: "secondary slot stacks below primary on narrow viewports",
    scrollModel: "single-or-independent-pane-scroll",
    densityProfile: "regular",
    applicableContentModes: ["detail", "checkout", "editor", "comparison"],
  },
  {
    patternId: "sidebar-workspace",
    layoutKind: "sidebar-workspace",
    slots: ["globalHeader", "sidebar", "workspace"],
    constraints: ["sidebar-full-height", "workspace-flexes", "header-global-not-page-local"],
    responsiveBehavior: "sidebar may collapse; workspace remains primary",
    scrollModel: "workspace-scrolls-independently",
    densityProfile: "dense-admin",
    applicableContentModes: ["admin", "analytics", "operations", "content-management"],
  },
  {
    patternId: "document-page",
    layoutKind: "document-page",
    slots: ["pageHeader", "title", "summary", "bodySections", "footnotes"],
    constraints: ["fixed-paper-ratio", "print-safe-margin", "page-break-aware"],
    responsiveBehavior: "paper canvas may scale or scroll; document hierarchy does not change",
    scrollModel: "document-canvas-scroll",
    densityProfile: "document",
    applicableContentModes: ["publication", "analysis", "proposal", "export-preview"],
  },
  {
    patternId: "dashboard-grid",
    layoutKind: "dashboard-grid",
    slots: ["filterBar", "kpiRow", "chartGrid", "dataRegion"],
    constraints: ["scan-friendly-alignment", "shared-time-or-filter-context", "chart-bounds-stable"],
    responsiveBehavior: "kpi and chart groups collapse predictably by breakpoint",
    scrollModel: "workspace-scroll",
    densityProfile: "dense-analytical",
    applicableContentModes: ["monitoring", "analytics", "operations"],
  },
  {
    patternId: "feed",
    layoutKind: "feed",
    slots: ["composer", "stream", "itemActions"],
    constraints: ["stable-item-width", "append-or-prepend-order", "item-boundary-clear"],
    responsiveBehavior: "stream width and side context adapt without changing item component vocabulary",
    scrollModel: "infinite-or-paged-scroll",
    densityProfile: "regular",
    applicableContentModes: ["social", "activity", "notifications", "news"],
  },
  {
    patternId: "multi-column",
    layoutKind: "multi-column",
    slots: ["lead", "secondary", "rail"],
    constraints: ["reading-order-preserved", "column-gap-tokenized", "lead-priority-clear"],
    responsiveBehavior: "columns collapse into reading-order stack",
    scrollModel: "page-scroll",
    densityProfile: "editorial",
    applicableContentModes: ["publishing", "article-index", "knowledge-base"],
  },
] as const;

export const APPLICATION_BLOCK_PATTERN_PROFILES: readonly BlockPatternProfile[] = [
  {
    patternId: "command-header",
    purpose: "Collect global commands, search, and quick actions without owning page content.",
    requiredComponents: ["container", "input"],
    optionalComponents: ["button", "nav", "text"],
    layoutPatternRefs: ["stack", "split"],
    dataInputs: ["currentRoute", "userContext", "commandHistory"],
    actionOutputs: ["submitCommand", "navigate", "openRecent"],
    designTokenRequirements: ["density", "surface", "spacing", "typography"],
  },
  {
    patternId: "sidebar-navigation",
    purpose: "Expose app navigation and workspace context as a reusable block.",
    requiredComponents: ["container", "nav", "button"],
    optionalComponents: ["text", "image", "list"],
    layoutPatternRefs: ["stack"],
    dataInputs: ["navItems", "currentRoute"],
    actionOutputs: ["navigate", "toggleCollapsed"],
    designTokenRequirements: ["density", "surface", "spacing", "stateStyleRule"],
  },
  {
    patternId: "filter-toolbar",
    purpose: "Capture query conditions and trigger datasource refresh.",
    requiredComponents: ["container", "input", "select", "button"],
    optionalComponents: ["checkbox", "switch", "text"],
    layoutPatternRefs: ["stack", "grid", "split"],
    dataInputs: ["queryState", "filterOptions"],
    actionOutputs: ["changeFilter", "submitQuery", "resetFilter"],
    designTokenRequirements: ["density", "spacing", "componentStyleRule"],
  },
  {
    patternId: "chart-table-composition",
    purpose: "Present a visual aggregate and its inspectable source rows at the same analytical grain.",
    requiredComponents: ["container", "chart", "data_table"],
    optionalComponents: ["text", "button", "tabs"],
    layoutPatternRefs: ["grid", "split", "document-page", "dashboard-grid"],
    dataInputs: ["chartRows", "chartSeries", "tableRows", "tableColumns"],
    actionOutputs: ["selectPoint", "sortTable", "drilldown"],
    designTokenRequirements: ["surface", "spacing", "typography", "componentStyleRule"],
  },
  {
    patternId: "data-grid-with-actions",
    purpose: "Show tabular records with row-level or bulk operations.",
    requiredComponents: ["container", "data_table", "button"],
    optionalComponents: ["input", "select", "tabs"],
    layoutPatternRefs: ["grid", "sidebar-workspace"],
    dataInputs: ["rows", "columns", "selectionState"],
    actionOutputs: ["selectRow", "bulkAction", "paginate", "sort"],
    designTokenRequirements: ["density", "surface", "spacing", "stateStyleRule"],
  },
  {
    patternId: "document-export-panel",
    purpose: "Expose document generation/export state without changing the source UI component vocabulary.",
    requiredComponents: ["container", "button", "text"],
    optionalComponents: ["data_table", "list"],
    layoutPatternRefs: ["stack", "split"],
    dataInputs: ["exportFormats", "exportStatus", "artifactRefs"],
    actionOutputs: ["exportDocument", "downloadArtifact", "showExportEvidence"],
    designTokenRequirements: ["surface", "spacing", "stateStyleRule"],
  },
  {
    patternId: "content-list",
    purpose: "List content records with readable hierarchy and optional grouping.",
    requiredComponents: ["container", "list", "text"],
    optionalComponents: ["image", "button", "tabs"],
    layoutPatternRefs: ["stack", "grid", "multi-column", "feed"],
    dataInputs: ["items", "grouping", "currentCategory"],
    actionOutputs: ["openItem", "filterCategory", "loadMore"],
    designTokenRequirements: ["typography", "spacing", "surface"],
  },
  {
    patternId: "detail-summary",
    purpose: "Render a selected entity or result with key fields and available actions.",
    requiredComponents: ["container", "text"],
    optionalComponents: ["image", "button", "data_table", "chart"],
    layoutPatternRefs: ["stack", "split", "document-page"],
    dataInputs: ["record", "fieldDefinitions", "relatedData"],
    actionOutputs: ["edit", "navigateRelated", "refresh"],
    designTokenRequirements: ["typography", "spacing", "surface"],
  },
] as const;

export const APPLICATION_PAGE_PATTERN_PROFILES: readonly PagePatternProfile[] = [
  {
    patternId: "admin-workspace-page",
    routeScope: "routable workspace in an authenticated or operational app",
    layoutPatternRefs: ["sidebar-workspace"],
    requiredBlockPatternRefs: ["command-header", "sidebar-navigation"],
    optionalBlockPatternRefs: ["filter-toolbar", "data-grid-with-actions", "chart-table-composition", "document-export-panel"],
    stateScope: "appData plus page-local state",
    actionScope: "navigation, query, mutation, export",
  },
  {
    patternId: "analysis-page",
    routeScope: "data analysis, publication preview, or export preview route",
    layoutPatternRefs: ["document-page", "dashboard-grid", "grid"],
    requiredBlockPatternRefs: ["filter-toolbar", "chart-table-composition"],
    optionalBlockPatternRefs: ["document-export-panel", "detail-summary"],
    stateScope: "query state plus derived analytical state",
    actionScope: "query, aggregate, drilldown, export",
  },
  {
    patternId: "content-index-page",
    routeScope: "content or listing entry point",
    layoutPatternRefs: ["multi-column", "feed", "grid"],
    requiredBlockPatternRefs: ["content-list"],
    optionalBlockPatternRefs: ["command-header", "filter-toolbar", "detail-summary"],
    stateScope: "category and pagination state",
    actionScope: "filter, open item, load more",
  },
  {
    patternId: "detail-page",
    routeScope: "single selected entity or content item",
    layoutPatternRefs: ["stack", "split", "document-page"],
    requiredBlockPatternRefs: ["detail-summary"],
    optionalBlockPatternRefs: ["content-list", "chart-table-composition", "document-export-panel"],
    stateScope: "route params plus selected record state",
    actionScope: "hydrate, navigate related, edit, export",
  },
  {
    patternId: "transaction-flow-page",
    routeScope: "transaction or checkout flow",
    layoutPatternRefs: ["split", "stack"],
    requiredBlockPatternRefs: ["detail-summary", "data-grid-with-actions"],
    optionalBlockPatternRefs: ["filter-toolbar", "document-export-panel"],
    stateScope: "cart/order draft state",
    actionScope: "update selection, submit transaction, show confirmation",
  },
] as const;

export const APPLICATION_INSTANCE_FIXTURES: readonly ApplicationInstanceFixture[] = [
  {
    fixtureId: "gridone-housing-analysis-app",
    domainLabel: "housing price analysis",
    pagePatternRefs: ["admin-workspace-page", "analysis-page"],
    blockPatternRefs: ["command-header", "sidebar-navigation", "filter-toolbar", "chart-table-composition", "document-export-panel"],
    layoutPatternRefs: ["sidebar-workspace", "document-page", "dashboard-grid"],
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    domainSpecificUiOntologyPromoted: false,
    designSystemValueSet: "reb-rone",
    dataModelLabel: "housing-price-index",
  },
  {
    fixtureId: "publishing-front-page-app",
    domainLabel: "news publishing",
    pagePatternRefs: ["content-index-page", "detail-page"],
    blockPatternRefs: ["command-header", "content-list", "detail-summary"],
    layoutPatternRefs: ["multi-column", "feed", "document-page"],
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    domainSpecificUiOntologyPromoted: false,
    designSystemValueSet: "editorial",
    dataModelLabel: "article-issue-author-category",
  },
  {
    fixtureId: "commerce-catalog-admin-app",
    domainLabel: "commerce operations",
    pagePatternRefs: ["admin-workspace-page", "transaction-flow-page"],
    blockPatternRefs: ["sidebar-navigation", "filter-toolbar", "data-grid-with-actions", "detail-summary"],
    layoutPatternRefs: ["sidebar-workspace", "grid", "split"],
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    domainSpecificUiOntologyPromoted: false,
    designSystemValueSet: "commerce-admin",
    dataModelLabel: "product-order-customer",
  },
] as const;

export const APPLICATION_COMPOSITION_METHODOLOGY_V1_CONTRACT = {
  schemaVersion: APPLICATION_COMPOSITION_METHODOLOGY_SCHEMA_VERSION,
  fixedAxes: {
    componentOntology: FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES,
    designSystemParts: FIXED_DESIGN_SYSTEM_PARTS,
  },
  variableAxes: [
    "layout-pattern",
    "ui-block-composition",
    "page-composition",
    "application-instance",
    "data-model",
    "actionflow-binding",
    "design-system-value-set",
  ],
  promotionRules: [
    "Do not promote domain names such as report, newspaper, REB, Gridone, ecommerce, or SNS into new component vocabulary.",
    "Promote only reusable layout/block/page composition patterns after mining and deterministic proof.",
    "Design system structure is fixed; only values and token bindings vary by application.",
    "A generated application is an instance using patterns, data, actionflow, and a design value set.",
  ],
  requiredProofStages: [
    "schema-validation",
    "composition-contract-validation",
    "layout-slot-validation",
    "block-page-reference-validation",
    "design-system-separation-validation",
    "instance-demotion-validation",
    "headless-render-proof",
  ],
} as const;

export const FORBIDDEN_CORE_PATTERN_DOMAIN_TERMS = [
  "report",
  "newspaper",
  "reb",
  "gridone",
  "commerce",
  "ecommerce",
  "sns",
  "real-estate",
  "housing",
] as const;

export type ApplicationCompositionValidationReport = {
  readonly ok: boolean;
  readonly errors: readonly string[];
  readonly summary: {
    readonly componentTypeCount: number;
    readonly designSystemPartCount: number;
    readonly layoutPatternCount: number;
    readonly blockPatternCount: number;
    readonly pagePatternCount: number;
    readonly applicationFixtureCount: number;
  };
};

export function validateApplicationCompositionMethodology(params: {
  readonly componentTypes?: readonly string[];
  readonly designSystemParts?: readonly string[];
  readonly layoutPatterns?: readonly LayoutPatternProfile[];
  readonly blockPatterns?: readonly BlockPatternProfile[];
  readonly pagePatterns?: readonly PagePatternProfile[];
  readonly applicationFixtures?: readonly ApplicationInstanceFixture[];
} = {}): ApplicationCompositionValidationReport {
  const componentTypes = params.componentTypes ?? FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES;
  const designSystemParts = params.designSystemParts ?? FIXED_DESIGN_SYSTEM_PARTS;
  const layoutPatterns = params.layoutPatterns ?? APPLICATION_LAYOUT_PATTERN_PROFILES;
  const blockPatterns = params.blockPatterns ?? APPLICATION_BLOCK_PATTERN_PROFILES;
  const pagePatterns = params.pagePatterns ?? APPLICATION_PAGE_PATTERN_PROFILES;
  const applicationFixtures = params.applicationFixtures ?? APPLICATION_INSTANCE_FIXTURES;
  const errors: string[] = [];

  assertSameSet("component ontology", componentTypes, FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES, errors);
  assertSameSet("design system part", designSystemParts, FIXED_DESIGN_SYSTEM_PARTS, errors);

  const layoutIds = new Set(layoutPatterns.map((pattern) => pattern.patternId));
  const blockIds = new Set(blockPatterns.map((pattern) => pattern.patternId));
  const pageIds = new Set(pagePatterns.map((pattern) => pattern.patternId));
  const componentIds = new Set(componentTypes);
  const designSystemPartIds = new Set(designSystemParts);

  for (const layout of layoutPatterns) {
    assertNoDomainSpecificPatternId("LayoutPattern", layout.patternId, errors);
    if (layout.slots.length === 0) errors.push(`LayoutPattern ${layout.patternId} must declare slots.`);
    if (layout.constraints.length === 0) errors.push(`LayoutPattern ${layout.patternId} must declare constraints.`);
    if (!layout.responsiveBehavior) errors.push(`LayoutPattern ${layout.patternId} must declare responsiveBehavior.`);
  }

  for (const block of blockPatterns) {
    assertNoDomainSpecificPatternId("BlockPattern", block.patternId, errors);
    for (const component of block.requiredComponents) {
      if (!componentIds.has(component)) errors.push(`BlockPattern ${block.patternId} references unknown component ${component}.`);
    }
    for (const component of block.optionalComponents) {
      if (!componentIds.has(component)) errors.push(`BlockPattern ${block.patternId} references unknown optional component ${component}.`);
    }
    for (const layoutRef of block.layoutPatternRefs) {
      if (!layoutIds.has(layoutRef)) errors.push(`BlockPattern ${block.patternId} references unknown layout ${layoutRef}.`);
    }
    for (const designRequirement of block.designTokenRequirements) {
      if (!designSystemPartIds.has(designRequirement)) {
        errors.push(`BlockPattern ${block.patternId} references unknown design system part ${designRequirement}.`);
      }
    }
    if (block.dataInputs.length === 0) errors.push(`BlockPattern ${block.patternId} must declare data inputs.`);
    if (block.actionOutputs.length === 0) errors.push(`BlockPattern ${block.patternId} must declare action outputs.`);
  }

  for (const page of pagePatterns) {
    assertNoDomainSpecificPatternId("PagePattern", page.patternId, errors);
    for (const layoutRef of page.layoutPatternRefs) {
      if (!layoutIds.has(layoutRef)) errors.push(`PagePattern ${page.patternId} references unknown layout ${layoutRef}.`);
    }
    for (const blockRef of [...page.requiredBlockPatternRefs, ...page.optionalBlockPatternRefs]) {
      if (!blockIds.has(blockRef)) errors.push(`PagePattern ${page.patternId} references unknown block ${blockRef}.`);
    }
  }

  for (const fixture of applicationFixtures) {
    if (fixture.componentVocabularyExtended) errors.push(`${fixture.fixtureId} must not extend component vocabulary.`);
    if (fixture.designSystemStructureExtended) errors.push(`${fixture.fixtureId} must not extend design system structure.`);
    if (fixture.domainSpecificUiOntologyPromoted) errors.push(`${fixture.fixtureId} must remain an application instance, not a domain UI ontology.`);
    for (const pageRef of fixture.pagePatternRefs) {
      if (!pageIds.has(pageRef)) errors.push(`${fixture.fixtureId} references unknown page pattern ${pageRef}.`);
    }
    for (const blockRef of fixture.blockPatternRefs) {
      if (!blockIds.has(blockRef)) errors.push(`${fixture.fixtureId} references unknown block pattern ${blockRef}.`);
    }
    for (const layoutRef of fixture.layoutPatternRefs) {
      if (!layoutIds.has(layoutRef)) errors.push(`${fixture.fixtureId} references unknown layout pattern ${layoutRef}.`);
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    summary: {
      componentTypeCount: componentTypes.length,
      designSystemPartCount: designSystemParts.length,
      layoutPatternCount: layoutPatterns.length,
      blockPatternCount: blockPatterns.length,
      pagePatternCount: pagePatterns.length,
      applicationFixtureCount: applicationFixtures.length,
    },
  };
}

function assertSameSet(
  label: string,
  actual: readonly string[],
  expected: readonly string[],
  errors: string[],
) {
  const actualSet = new Set(actual);
  const expectedSet = new Set(expected);
  for (const item of actualSet) {
    if (!expectedSet.has(item)) errors.push(`Unexpected ${label}: ${item}`);
  }
  for (const item of expectedSet) {
    if (!actualSet.has(item)) errors.push(`Missing ${label}: ${item}`);
  }
}

function assertNoDomainSpecificPatternId(
  label: string,
  patternId: string,
  errors: string[],
) {
  for (const forbiddenTerm of FORBIDDEN_CORE_PATTERN_DOMAIN_TERMS) {
    if (patternId.includes(forbiddenTerm)) {
      errors.push(`${label} ${patternId} must not promote domain term ${forbiddenTerm} into core pattern ontology.`);
    }
  }
}
